import api, {getBrowserCountryCode} from './Api';

export const COUNTRY_CONFIGURATION_ENDPOINT = '/api/v1/storefront/country-configuration';
const sessions = new Map();
const rejectedCountryIds = new Map();
const validId = value => /^\d+$/.test(String(value || '')) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
const code = value => /^[A-Z]{2,3}$/.test(String(value || '').toUpperCase()) ? String(value).toUpperCase() : '';
const ttl = 5 * 60 * 1000;
let revision = 0;

export function countryConfigurationContext(overrides = {}) {
    const countryId = overrides.countryId ?? localStorage.getItem('country_id');
    const countryCode = code(overrides.countryCode || localStorage.getItem('country_code') || getBrowserCountryCode?.());
    return {
        countryId:validId(countryId) && !(rejectedCountryIds.get(Number(countryId)) > Date.now()) ? Number(countryId) : null,
        countryCode,
        selectedCountryCode:code(overrides.selectedCountryCode ?? localStorage.getItem('country_code')),
        currency:String(overrides.currency ?? localStorage.getItem('currency_code') ?? '').toUpperCase(),
        deliveryCountry:code(overrides.deliveryCountry),
    };
}
export const countryConfigurationKey = context => JSON.stringify([context.countryId || context.countryCode, context.selectedCountryCode, context.currency, context.deliveryCountry]);

export function invalidateCountryConfiguration() {
    revision++;
    for (const session of sessions.values()) session.controller?.abort();
    sessions.clear();
}

function retryDelay(error, failures) {
    const raw = error.response?.headers?.['retry-after'];
    const seconds = Number(raw);
    const provider = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : Date.parse(raw) - Date.now();
    return Math.min(5 * 60 * 1000, Math.max(1000, Number.isFinite(provider) ? provider : 1000 * 2 ** Math.min(failures, 8)));
}

// Optional presentation metadata only. Backend order pricing always resolves its own trusted profile.
export function loadCountryConfiguration(input = {}) {
    const context = countryConfigurationContext(input);
    const key = countryConfigurationKey(context);
    if (!context.countryId && !context.countryCode) return Promise.resolve(null);
    let session = sessions.get(key);
    if (session?.promise) return session.promise;
    if (session && session.until > Date.now()) return Promise.resolve(session.value);
    session = {value:null,failures:session?.failures || 0,controller:new AbortController()};
    sessions.set(key, session);
    const generation = revision;
    const rejectSelectedId = () => {
        if (context.countryId) rejectedCountryIds.set(context.countryId,Date.now() + 30000);
        if (context.countryId && localStorage.getItem('country_id') === String(context.countryId)) localStorage.removeItem('country_id');
        window.dispatchEvent(new CustomEvent('country-resolution-required', {detail:{countryCode:context.countryCode}}));
    };
    const params = context.countryId ? {country_id:context.countryId} : {country_code:context.countryCode};
    session.promise = Promise.resolve().then(() => api.get(COUNTRY_CONFIGURATION_ENDPOINT, {params,publicRequest:true,signal:session.controller.signal})).then(response => {
        if (generation !== revision || session.controller.signal.aborted) return null;
        const payload = response.data;
        const data = payload?.data;
        const country = data?.country;
        const matches = context.countryId ? Number(country?.id) === context.countryId : [country?.country_code,country?.country_code_alpha3].some(value => code(value) === context.countryCode);
        const selectedCodeMatches = !context.selectedCountryCode || [country?.country_code,country?.country_code_alpha3].some(value => code(value) === context.selectedCountryCode);
        if (payload?.success === true && country?.country_code && !selectedCodeMatches && context.countryId) {
            session.until = Date.now() + 30000;
            rejectSelectedId();
            return null;
        }
        const nestedMatches = [data?.legal_tax_profile,data?.default_warehouse].every(record => !record || record.country_id == null || Number(record.country_id) === Number(country?.id));
        if (payload?.success !== true || !matches || !selectedCodeMatches || !nestedMatches || (context.currency && String(country?.currency_code).toUpperCase() !== context.currency)) {
            session.until = Date.now() + 30000;
            return null;
        }
        session.value = data; session.until = Date.now() + ttl; session.failures = 0;
        return data;
    }).catch(error => {
        if (generation !== revision || session.controller.signal.aborted) return null;
        session.failures++;
        session.until = Date.now() + (error.response?.status === 429 ? retryDelay(error,session.failures) : 30000);
        if (error.response?.status === 422) {
            rejectSelectedId();
        }
        return null;
    }).finally(() => {session.promise = null;});
    return session.promise;
}

for (const event of ['country-changed','currency-changed','delivery-country-changed']) window.addEventListener(event,invalidateCountryConfiguration);
window.addEventListener('storage', event => {
    if (['country_id','country_code','currency_code'].includes(event.key)) invalidateCountryConfiguration();
});
